import 'mapbox-gl/dist/mapbox-gl.css'

import { useMemo, useRef, useState } from 'react'
import Map, { Layer, MapMouseEvent, MapRef, Marker, Popup, Source } from 'react-map-gl/mapbox'
import { DEFAULT_THEME, useComputedColorScheme } from '@mantine/core'

import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import {
  NavigationStreetSegment,
  UnexpectedEventNavigationData,
  UnexpectedEventNavigationPatrolVehicle,
} from '@/api/main/types.gen'

import CopyableId from './CopyableId'
import { EventLocation } from './eventLocation'

type Props = {
  id: string
  navigationData?: UnexpectedEventNavigationData
  patrolVehicle?: UnexpectedEventNavigationPatrolVehicle
  position?: EventLocation
  initialZoom?: number
  onZoomChange?: (zoom: number) => void
  height?: number | string
  width?: number | string
}

const DEFAULT_CENTER = { longitude: 14.41204, latitude: 50.01079 }

type LineFeature = GeoJSON.Feature<GeoJSON.LineString, { color: string; streetSegmentId: string }>

const segmentCoords = (segment: NavigationStreetSegment): Array<[number, number]> => {
  const points = segment.geom.points.map(p => [p.longitude, p.latitude] as [number, number])
  if (!segment.direction_forward) points.reverse()
  return points
}

// Progressive coloring along the planned route, matching the legacy map-ui scheme:
// the closest upcoming (last in the reversed list) segment is red, the one after it is yellow,
// everything further out is light blue. Kept in sync with mapGLHelpers.ts in map-ui.
const futureSegmentColor = (index: number, total: number): string => {
  if (index === total - 1) return DEFAULT_THEME.colors.red[6]
  if (index === total - 2) return DEFAULT_THEME.colors.yellow[6]
  return DEFAULT_THEME.colors.blue[3]
}

const CURRENT_SEGMENT_COLOR = DEFAULT_THEME.colors.violet[5]

const StaticNavigationMap = ({
  id,
  navigationData,
  patrolVehicle,
  position,
  initialZoom = 16,
  onZoomChange,
  height = 360,
  width = '100%',
}: Props) => {
  const colorScheme = useComputedColorScheme('light')
  const [popupSegment, setPopupSegment] = useState<{ id: string; longitude: number; latitude: number } | null>(null)
  const mapRef = useRef<MapRef | null>(null)

  const futureFeatures = useMemo<GeoJSON.FeatureCollection<GeoJSON.LineString, LineFeature['properties']>>(() => {
    const segments = navigationData?.future_street_segments ?? []
    // Reverse so that the *closest* upcoming segment sits at the end of the list,
    // matching the index logic in `futureSegmentColor`.
    const ordered = [...segments].reverse()
    const features: Array<LineFeature> = ordered.map((segment, index) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: segmentCoords(segment) },
      properties: {
        color: futureSegmentColor(index, ordered.length),
        streetSegmentId: segment.street_segment_id,
      },
    }))
    return { type: 'FeatureCollection', features }
  }, [navigationData?.future_street_segments])

  const currentFeatures = useMemo<GeoJSON.FeatureCollection<GeoJSON.LineString, LineFeature['properties']>>(() => {
    const segment = navigationData?.current_street_segment
    if (!segment) return { type: 'FeatureCollection', features: [] }
    const feature: LineFeature = {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: segmentCoords(segment) },
      properties: { color: CURRENT_SEGMENT_COLOR, streetSegmentId: segment.street_segment_id },
    }
    return { type: 'FeatureCollection', features: [feature] }
  }, [navigationData?.current_street_segment])

  const labelFeatures = useMemo<GeoJSON.FeatureCollection<GeoJSON.LineString, { streetSegmentId: string }>>(() => {
    const seen = new Set<string>()
    const features: Array<GeoJSON.Feature<GeoJSON.LineString, { streetSegmentId: string }>> = []
    const consider = (segment?: NavigationStreetSegment) => {
      if (!segment) return
      if (seen.has(segment.street_segment_id)) return
      seen.add(segment.street_segment_id)
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: segmentCoords(segment) },
        properties: { streetSegmentId: segment.street_segment_id },
      })
    }
    consider(navigationData?.current_street_segment)
    for (const seg of navigationData?.future_street_segments ?? []) consider(seg)
    return { type: 'FeatureCollection', features }
  }, [navigationData?.current_street_segment, navigationData?.future_street_segments])

  const center = position ?? DEFAULT_CENTER

  const handleMapClick = (event: MapMouseEvent) => {
    // Hit-test manually rather than via `interactiveLayerIds`, which would also
    // flip mapbox-gl's cursor to a pointer on segment hover (we want to match
    // pigeon-maps' plain grab/grabbing cursor — see DriveSegmentsMap).
    const map = mapRef.current?.getMap()
    if (!map) return
    const features = map.queryRenderedFeatures(event.point, {
      layers: ['lineLayer', 'currentSegment'],
    })
    if (features.length === 0) {
      setPopupSegment(null)
      return
    }
    const props = features[0].properties as { streetSegmentId?: string } | null
    if (!props?.streetSegmentId) return
    setPopupSegment({
      id: props.streetSegmentId,
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    })
  }

  return (
    <Map
      // Remount when the target event changes so `initialViewState` kicks in with the new center.
      // Within the same event we stay uncontrolled, which is what lets the user drag-pan freely.
      key={id}
      id={id}
      initialViewState={{
        longitude: center.longitude,
        latitude: center.latitude,
        zoom: initialZoom,
      }}
      style={{ width, height }}
      mapStyle={`mapbox://styles/mapbox/${colorScheme === 'dark' ? 'dark-v11' : 'streets-v12'}`}
      mapboxAccessToken={MAPBOX_PUBLIC_TOKEN ?? undefined}
      ref={mapRef}
      dragPan
      scrollZoom
      touchZoomRotate
      onZoomEnd={e => onZoomChange?.(e.viewState.zoom)}
      onClick={handleMapClick}
    >
      <Source id="futureSegments" type="geojson" data={futureFeatures}>
        <Layer
          id="lineLayer"
          type="line"
          layout={{ 'line-join': 'round', 'line-cap': 'round' }}
          paint={{ 'line-color': ['get', 'color'], 'line-width': 8 }}
        />
      </Source>

      <Source id="currentSegmentSource" type="geojson" data={currentFeatures}>
        <Layer
          id="currentSegment"
          type="line"
          layout={{ 'line-join': 'round', 'line-cap': 'round' }}
          paint={{ 'line-color': ['get', 'color'], 'line-width': 8 }}
        />
      </Source>

      <Source id="segmentLabels" type="geojson" data={labelFeatures}>
        <Layer
          id="segmentLabels"
          type="symbol"
          layout={{
            'symbol-placement': 'line',
            'text-field': ['get', 'streetSegmentId'],
            'text-size': 11,
            'text-allow-overlap': false,
          }}
          paint={{
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 1.5,
          }}
        />
      </Source>

      {patrolVehicle?.position?.latitude !== undefined &&
        patrolVehicle?.position?.longitude !== undefined &&
        patrolVehicle.position.latitude !== null &&
        patrolVehicle.position.longitude !== null && (
          <Marker
            longitude={patrolVehicle.position.longitude!}
            latitude={patrolVehicle.position.latitude!}
            anchor="center"
          >
            <svg width={36} height={36} style={{ transform: `rotate(${patrolVehicle.azimuth ?? 0}deg)` }}>
              <circle cx={18} cy={18} r={14} fill="#fd7e14" />
              <polygon points="18,4 28,28 18,22 8,28" fill="#fff" />
            </svg>
          </Marker>
        )}

      {popupSegment && (
        <Popup
          longitude={popupSegment.longitude}
          latitude={popupSegment.latitude}
          anchor="bottom"
          onClose={() => setPopupSegment(null)}
          closeOnClick={false}
        >
          <CopyableId value={popupSegment.id} />
        </Popup>
      )}
    </Map>
  )
}

export default StaticNavigationMap
